import {
  Skin,
  Spine,
  SpineDebugRenderer,
  SpineFromOptions,
  SpineTexture,
  TextureAtlas
} from '@esotericsoftware/spine-pixi-v8';
import {
  assetConfigImageLiteral,
  assetManifestPixiLiteral,
  ConfigSwatchKeys,
  configSwatchLiteral,
  isKeyOfImageConfig
} from '@nova/configs';
import {
  BotPartsConfig,
  BotPartSlot,
  CompletePartDataSet,
  EResourceType,
  IConfigResourceRef,
  PipelineSeriesByRegionColour
} from '@nova/types';
import { Logger, RemoveExtension, ReplaceAll } from '@nova/utils';
import {
  Application,
  Assets,
  Container,
  ExtensionFormatLoose,
  extensions,
  LoaderParser,
  Texture,
  TextureSource,
  UnresolvedAsset
} from 'pixi.js';

import { GameManager } from '../core';
import { AssetUtils } from '../core/assets/AssetUtils';

import {
  BotSpineSource,
  type BotOptions,
  DEFAULT_BOT_SPINE_CDN_OPTIONS,
  MANIFEST_BOT_SPINE,
  type TeamColorAssignment
} from './bot-defaults';
import { ORDERED_BOT_PART_SLOTS, BOT_SKIN_COMPOSITION_ORDER } from './bot-slots';
import { customSpineTextureAtlasLoader, ExtendedTextureAtlas } from './loader/CustomSpineTextureAtlasLoader';
import { createRandomPartDataSet } from './utils/BotPartUtils';

export type { BotOptions, TeamColorAssignment } from './bot-defaults';
export { DEFAULT_BOT_SPINE_CDN_OPTIONS, DEFAULT_BOT_TEAM_COLOR, MANIFEST_BOT_SPINE } from './bot-defaults';
export { ORDERED_BOT_PART_SLOTS, BOT_SKIN_COMPOSITION_ORDER } from './bot-slots';
export {
  createRandomPartData,
  createRandomPartDataSet,
  resolveSlotsToRandomize
} from './utils/BotPartUtils';

export type PageReplacement = { image: Texture | string; originalName: string };

export type CustomAtlasOptions = {
  atlasName: string; // The unique name to store in cache for the custom atlas.
  pages: Record<string, PageReplacement>; // The pages to replace in the atlas.
};

export type SpineLoadOptions = SpineFromOptions & {
  customAtlasOptions?: CustomAtlasOptions;
  scale?: number;
};

export interface SkinPart {
  part: string;
  skinName: string;
  slot: BotPartSlot;
}

export interface CustomBotSkinData {
  partSkins: string[];
  customSkinName: string;
}

export type CustomPageReplacement = {
  key: string;
  swatch?: PipelineSeriesByRegionColour;
  alias: string;
  base: string;
  mask?: string;
  originalName: string;
  bakeTeamColor?: boolean;
};

export class SpineObjectFactory {
  public static app?: Application;

  private static readonly _textureAtlasExtensionName = 'CustomSpineTextureAtlasLoader';
  private static readonly _defaultSpineBundleName = 'PlayerBotSpines';

  private static _debugEnabled = false;
  private static _spineDebugRenderer: SpineDebugRenderer;

  public static async initializeStandAlone(app: Application): Promise<void> {
    if (GameManager.app) {
      throw new Error('SpineObjectFactory::initializeStandAlone: GameManager.app is already initialized. ' +
        'This method should only be called when the spine project is being used.');
    }
    SpineObjectFactory.app = app;
    await AssetUtils.setStandaloneApp(app);
  }

  /**
   * Creates a Spine object asynchronously.
   * If customAtlasName is provided, it will load the custom atlas and use it to create the Spine object.
   *
   * @param options - The options for loading the Spine object.
   * @returns A promise that resolves to a Spine object.
   */
  public static async load(options: SpineLoadOptions): Promise<Spine> {
    const customAtlasOptions = options?.customAtlasOptions;
    await Promise.all([
      !Assets.cache.has(options.skeleton) && Assets.load(options.skeleton),
      customAtlasOptions && !Assets.cache.has(customAtlasOptions.atlasName) && this.loadCustomTextureAtlas(options.atlas, customAtlasOptions)
    ]);

    const spine = Spine.from(options);
    if (this._debugEnabled) {
      spine.debug = this._spineDebugRenderer;
    }

    return spine;
  }

  /**
   * Retrieves a Spine object based on the provided options.
   *
   * @param options - Optional parameters for loading the Spine object.
   * @returns The created Spine object.
   */
  public static get(options: SpineLoadOptions): Spine {
    const spine = Spine.from({ ...options, atlas: options.customAtlasOptions?.atlasName ?? options.atlas });

    if (this._debugEnabled) {
      spine.debug = this._spineDebugRenderer;
    }

    return spine;
  }

  public static setDebug(enabled: boolean): void {
    this._debugEnabled = enabled;
    if (this._debugEnabled && !this._spineDebugRenderer) {
      this._spineDebugRenderer = new SpineDebugRenderer();
    }
  }

  /**
   * Utility function to load the bot spine from the PlayerBotSpines bundle.
   *
   * @param botSpineOptions - The SpineFromOptions for the bot skeleton and atlas.
   * @throws Error if the PlayerBotSpines bundle is not found in the manifest.
   */
  private static async loadBotSpine(botSpineOptions?: SpineFromOptions) {
    const atlasLoader: LoaderParser = Assets.loader.parsers.find((parser) =>
      parser.extension && (parser.extension as ExtensionFormatLoose).name === this._textureAtlasExtensionName) as LoaderParser;

    if (!atlasLoader) {
      extensions.add(customSpineTextureAtlasLoader);
    }

    if (!botSpineOptions) {
      const botBundle = assetManifestPixiLiteral.bundles.find((bundle) => bundle.name === this._defaultSpineBundleName);
      if (!botBundle) {
        throw new Error(`${this._defaultSpineBundleName} bundle not found in manifest config`);
      }
      await Assets.loadBundle(botBundle.name);
    }
    else {
      await Promise.all([
        !Assets.cache.has(botSpineOptions.skeleton) && Assets.load({ src: botSpineOptions.skeleton, alias: 'botSkeleton' }),
        !Assets.cache.has(botSpineOptions.atlas) && Assets.load({ src: botSpineOptions.atlas, alias: 'botAtlas' })
      ]);
    }
  }

  /**
   * Loads bot skeleton/atlas assets and caches customized atlas pages for one or many bots.
   */
  public static async loadBotAssets(
    partsOrPartsList: CompletePartDataSet | CompletePartDataSet[],
    spineSource?: BotSpineSource,
    options?: BotOptions
  ): Promise<void> {
    const partsList = Array.isArray(partsOrPartsList) ? partsOrPartsList : [partsOrPartsList];

    await SpineObjectFactory.loadBotSpine(SpineObjectFactory.resolveBotSpineSource(spineSource));

    if (partsList.length > 0) {
      await SpineObjectFactory.loadBotPartImages(partsList, options);
      await Promise.all(partsList.map((parts) => SpineObjectFactory.loadBotAtlas(parts, options)));
    }
  }

  private static async loadBotPartImages(
    partsList: CompletePartDataSet[],
    options?: BotOptions
  ): Promise<void> {
    const assetsByAlias = new Map<string, UnresolvedAsset>();

    for (const parts of partsList) {
      const { assetsToLoad } = SpineObjectFactory.prepareBotPartPages(
        parts,
        options?.teamColor,
        options?.visibleSlots
      );
      for (const asset of assetsToLoad) {
        assetsByAlias.set(asset.alias as string, asset);
      }
    }

    if (assetsByAlias.size > 0) {
      await Assets.load([...assetsByAlias.values()]);
    }
  }

  private static async loadBotAtlas(
    parts: CompletePartDataSet,
    options?: BotOptions
  ): Promise<void> {
    const atlasKeyOptions = SpineObjectFactory.getBotAtlasKeyOptions(options);
    const atlasName = AssetUtils.getBotAtlasKey(parts, atlasKeyOptions);

    if (Assets.cache.has(atlasName)) {
      return;
    }

    const newPages = await SpineObjectFactory.loadBotPartPages(
      parts,
      options?.teamColor,
      options?.visibleSlots
    );

    await SpineObjectFactory.loadCustomTextureAtlas('botAtlas', { atlasName, pages: newPages });
  }

  /**
   * Creates a bot Spine instance with composed part skins. Assets must already be loaded.
   */
  public static createBot(
    parts: CompletePartDataSet,
    options?: BotOptions,
    parent?: Container
  ): Spine {
    const atlasKeyOptions = SpineObjectFactory.getBotAtlasKeyOptions(options);
    const atlasName = AssetUtils.getBotAtlasKey(parts, atlasKeyOptions);

    if (!Assets.cache.has(atlasName)) {
      const pages = SpineObjectFactory.getBotPartPages(
        parts,
        options?.teamColor,
        options?.visibleSlots
      );
      SpineObjectFactory.createCustomTextureAtlas('botAtlas', { atlasName, pages });
    }

    const customBotSkinData = SpineObjectFactory.getPartsCustomSkins(parts, options?.visibleSlots);
    const bot = SpineObjectFactory.get({ skeleton: 'botSkeleton', atlas: atlasName });
    let customSkin = bot.skeleton.data.findSkin(customBotSkinData.customSkinName) as Skin;
    if (!customSkin) {
      customSkin = new Skin(customBotSkinData.customSkinName);

      const skinParts = SpineObjectFactory.getBotSkinParts(parts, options?.visibleSlots);

      skinParts.forEach(({ skinName }) => {
        const skin = bot.skeleton.data.findSkin(skinName);
        if (!skin) {
          throw new Error(`Skin not found: ${skinName}`);
        }
        customSkin.addSkin(skin);
      });
    }

    bot.skeleton.setSkin(customSkin);
    bot.skeleton.setSlotsToSetupPose();
    SpineObjectFactory.applyBotHeadAccessoryVisibility(bot, options);
    bot.state.setAnimation(0, '.idle', true);

    if (SpineObjectFactory._debugEnabled) {
      bot.debug = SpineObjectFactory._spineDebugRenderer;
    }

    if (parent) {
      parent.addChild(bot);
    }

    return bot;
  }

  public static async loadAndCreateBot(
    parts: CompletePartDataSet,
    spineSource?: BotSpineSource,
    options?: BotOptions,
    parent?: Container
  ): Promise<Spine> {
    await SpineObjectFactory.loadBotAssets(parts, spineSource, options);
    return SpineObjectFactory.createBot(parts, options, parent);
  }

  public static async loadRandomBot(
    config: BotPartsConfig,
    spineSource?: BotSpineSource,
    options?: BotOptions
  ): Promise<Spine> {
    const parts = createRandomPartDataSet(config, options);
    return SpineObjectFactory.loadAndCreateBot(parts, spineSource, options);
  }

  public static async loadRandomBotAssets(
    count: number,
    config: BotPartsConfig,
    spineSource?: BotSpineSource,
    options?: BotOptions
  ): Promise<CompletePartDataSet[]> {
    const partsList = Array.from({ length: count }, () => createRandomPartDataSet(config, options));

    await SpineObjectFactory.loadBotAssets(partsList, spineSource, options);

    return partsList;
  }

  private static resolveBotSpineSource(spineSource?: BotSpineSource): SpineFromOptions | undefined {
    if (spineSource === MANIFEST_BOT_SPINE) {
      return undefined;
    }

    if (spineSource) {
      return spineSource;
    }

    return DEFAULT_BOT_SPINE_CDN_OPTIONS;
  }

  /**
   * Retrieves the custom bot skin data for the given parts.
   * @param parts - The complete part data set.
   * @returns The custom bot skin data containing the part skins and the custom skin name.
   */
  private static getPartsCustomSkins(parts: CompletePartDataSet, visibleSlots?: BotPartSlot[]): CustomBotSkinData {
    const partSkins: string[] = [];
    let customSkinName = 'customSkin';
    const visibleSlotSet = visibleSlots?.length ? new Set(visibleSlots) : undefined;

    for (const skinPart of SpineObjectFactory.getBotSkinParts(parts, visibleSlots)) {
      partSkins.push(skinPart.skinName);
      customSkinName += `.${skinPart.skinName}`;
    }

    if (visibleSlotSet) {
      customSkinName += `.visible.${[...visibleSlotSet].sort().join('.')}`;
    }

    return { partSkins, customSkinName };
  }

  private static getBotSkinParts(parts: CompletePartDataSet, visibleSlots?: BotPartSlot[]): SkinPart[] {
    const visibleSlotSet = visibleSlots?.length ? new Set(visibleSlots) : undefined;

    return BOT_SKIN_COMPOSITION_ORDER
      .map(({ slot, spinePartName }) => ({
        part: spinePartName,
        slot,
        skinName: parts[slot].partSkin ?? ''
      }))
      .filter(({ slot, skinName }) => {
        if (!skinName) {
          return false;
        }

        return !visibleSlotSet || visibleSlotSet.has(slot);
      });
  }

  private static applyBotHeadAccessoryVisibility(bot: Spine, options?: BotOptions): void {
    const visibleSlots = options?.visibleSlots;
    if (!visibleSlots?.includes(BotPartSlot.HELMET)) {
      return;
    }

    if (options?.includeHeadAccessories !== false) {
      return;
    }

    for (const slotName of ['eyes', 'lights']) {
      bot.skeleton.findSlot(slotName)?.setAttachment(null);
    }
  }

  /**
   * Loads a custom texture atlas for a Spine object.
   * It loads the atlas, modifies the raw atlas data, and creates a custom atlas using the provided options.
   * This new atlas is then cached with the customAtlasName.
   *
   * @param atlasAssetName - The name of the atlas asset to load.
   * @param options - The options for loading the atlas.
   * @param force - Whether to override the cached custom atlas.
   * @returns A promise that resolves when the custom atlas is loaded.
   * @throws Error if the atlas loader/parser for Spine is not found.
   */
  public static async loadCustomTextureAtlas(atlasAssetName: string, options: CustomAtlasOptions): Promise<void> {
    // ? - Unfortunately we have to fetch this loader/parser from the global loader, as it's not exported from the spine-pixi package.
    const atlasLoader: LoaderParser = Assets.loader.parsers.find((parser) =>
      parser.extension && (parser.extension as ExtensionFormatLoose).name === this._textureAtlasExtensionName) as LoaderParser;

    if (!atlasLoader.load || !atlasLoader.parse) { throw new Error('Spine atlas loader/parser not found'); }

    const atlasText: string = Assets.cache.has(atlasAssetName)
      ? Assets.cache.get<ExtendedTextureAtlas>(atlasAssetName).rawText
      : await atlasLoader.load(atlasAssetName);

    const modifiedStringPairs = this.createModifiedStringPairs(options.pages);
    const modifiedRawAtlas = ReplaceAll(atlasText, modifiedStringPairs);

    const newPages = this.createNewPages({ pages: options.pages });
    const customAtlas = await this.loadCustomAtlas(atlasLoader, modifiedRawAtlas, atlasAssetName, newPages) as ExtendedTextureAtlas; /* eslint-disable-line -- disabling for extended typing */
    customAtlas.rawText = modifiedRawAtlas;

    Assets.cache.set(options.atlasName, customAtlas);
  }

  /**
   * Creates a custom texture atlas based on the provided atlas asset name and options.
   *
   * @param atlasAssetName - The name of the atlas asset.
   * @param options - The options for creating the custom atlas.
   */
  private static createCustomTextureAtlas(atlasAssetName: string, options: CustomAtlasOptions) {
    const atlasText: string = Assets.cache.get<ExtendedTextureAtlas>(atlasAssetName).rawText;

    const modifiedStringPairs = this.createModifiedStringPairs(options.pages);
    const modifiedRawAtlas = ReplaceAll(atlasText, modifiedStringPairs);

    const newPages = this.createNewPages({ pages: options.pages });
    const customAtlas = new TextureAtlas(modifiedRawAtlas) as ExtendedTextureAtlas;
    customAtlas.rawText = modifiedRawAtlas;

    // fill the pages
    for (const page of customAtlas.pages) {
      const pageName = page.name;
      const providedPage = newPages[pageName] || undefined;
      if (providedPage instanceof Texture) {
        page.setTexture(SpineTexture.from(providedPage.source));
      }
    }

    Assets.cache.set(options.atlasName, customAtlas);
  }

  /**
   * Creates a modified string pairs object based on the given pages.
   * The modified string pairs object maps original page names to modified page names.
   *
   * @param pages - The pages object containing original and modified page names.
   * @returns The modified string pairs object.
   */
  private static createModifiedStringPairs(pages: Record<string, PageReplacement>): Record<string, string> {
    return Object.fromEntries(
      Object.entries(pages).map(([key, { originalName }]) => [
        RemoveExtension(originalName),
        RemoveExtension(key)
      ])
    );
  }

  /**
   * Creates new pages based on the provided configuration.
   *
   * @param pages - The pages configuration.
   * @returns An object containing the created pages.
   */
  private static createNewPages({ pages }: { pages: Record<string, PageReplacement> }): Record<string, Texture | string> {
    return Object.fromEntries(
      Object.entries(pages).map(([key, { image }]) => [key, image])
    );
  }

  /**
   * Creates a custom atlas using the provided parameters.
   *
   * @param atlasLoader The atlas loader object.
   * @param modifiedRawAtlas The modified raw atlas data.
   * @param atlasAssetName The name of the atlas asset.
   * @param newPages The new pages for the atlas.
   * @returns A promise that resolves to the parsed atlas.
   */
  private static async loadCustomAtlas(
    atlasLoader: LoaderParser,
    modifiedRawAtlas: string,
    atlasAssetName: string,
    newPages: Record<string, Texture | string>
  ): Promise<TextureAtlas> {
    if (!atlasLoader.parse) { throw new Error(`Parser for ${atlasLoader.id} not found`); }

    return atlasLoader.parse(modifiedRawAtlas, {
      src: Assets.resolver.resolveUrl(atlasAssetName) as string,
      data: {
        images: newPages
      }
    }, Assets.loader);
  }

  /**
   * Generates new atlas pages for bot parts based on the provided parts dataset.
   *
   * @param parts - The complete part dataset.
   * @returns A promise that resolves to a record of new pages with their replacements.
   */
  private static prepareBotPartPages(
    parts: CompletePartDataSet,
    teamColor?: TeamColorAssignment,
    visibleSlots?: BotPartSlot[]
  ): { newPagesData: CustomPageReplacement[]; assetsToLoad: UnresolvedAsset[] } {
    const newPagesData = [];
    const assetsByAlias = new Map<string, UnresolvedAsset>();
    const visibleSlotSet = visibleSlots?.length ? new Set(visibleSlots) : undefined;

    const queueImageAsset = (datasetKey: string): void => {
      if (Assets.cache.has(datasetKey) || assetsByAlias.has(datasetKey)) {
        return;
      }

      if (!isKeyOfImageConfig(datasetKey)) {
        Logger.error(`SpineObjectFactory::prepareBotPartPages: datasetKey <${datasetKey}> is not in the image config.`);
        return;
      }

      const [asset] = this.getSourceFromImageResource({ type: EResourceType.image, datasetKey });
      assetsByAlias.set(datasetKey, asset);
    };

    for (const slot of ORDERED_BOT_PART_SLOTS) {
      if (visibleSlotSet && !visibleSlotSet.has(slot)) {
        continue;
      }
      const part = parts[slot];
      const partType = slot;
      if (!part.swatchMaskDatasetKey) {
        Logger.error(`SpineObjectFactory::prepareBotPartPages: Part <${partType}> does not have a swatchMaskDatasetKey`);
        continue;
      }

      if (!isKeyOfImageConfig(part.swatchMaskDatasetKey) || !isKeyOfImageConfig(part.datasetKey)) {
        Logger.error(`SpineObjectFactory::prepareBotPartPages: Part <${partType}> swatchMaskDatasetKey or datasetKey is not in the image config.`);
        continue;
      }

      queueImageAsset(part.swatchMaskDatasetKey);
      queueImageAsset(part.datasetKey);

      // Generate swatch
      const swatchData = configSwatchLiteral[part.swatchKey as ConfigSwatchKeys];
      if (!swatchData || Object.keys(swatchData).length === 0) {
        Logger.error(`SpineObjectFactory::prepareBotPartPages: Part <${partType}> does not have valid swatchData.`);
        continue;
      }

      // Generate new atlas pages for bot parts
      for (const resourceKey in part.resources) {
        if (!resourceKey) { continue; }
        const resource = part.resources[resourceKey];
        if (!resource) { continue; }

        if (resourceKey === 'defaultTexture') {
          const tintSlot = resource.stateUsage.tintSlot;
          const shouldBakeTeamColor = !!(teamColor && tintSlot && part.tintSlots?.includes(tintSlot));
          // Default texture uses mask and swatch
          newPagesData.push({
            key: `${resource.datasetKey}.png`,
            swatch: swatchData as unknown as PipelineSeriesByRegionColour,
            alias: AssetUtils.getSwatchAssetKeyName(part.datasetKey, part.swatchKey),
            base: part.datasetKey,
            mask: part.swatchMaskDatasetKey,
            originalName: resource.stateUsage.replace as string,
            bakeTeamColor: shouldBakeTeamColor
          });
        }
        else if (resourceKey === 'eyes') {
          const tintSlot = resource.stateUsage.tintSlot;
          const shouldBakeTeamColor = !!(teamColor && tintSlot && part.tintSlots?.includes(tintSlot));
          // Eye textures don't use mask/swatch
          queueImageAsset(resource.datasetKey);

          newPagesData.push({
            key: `${resource.datasetKey}.png`,
            alias: resource.datasetKey,
            base: resource.datasetKey,
            originalName: resource.stateUsage.replace as string,
            bakeTeamColor: shouldBakeTeamColor
          });
        }
      }
    }

    return { newPagesData, assetsToLoad: [...assetsByAlias.values()] };
  }

  /**
   * Loads the bot part pages asynchronously.
   *
   * @param parts - The complete part data set.
   * @returns A promise that resolves to a record of page replacements.
   */
  private static async loadBotPartPages(
    parts: CompletePartDataSet,
    teamColor?: TeamColorAssignment,
    visibleSlots?: BotPartSlot[]
  ): Promise<Record<string, PageReplacement>> {
    const { newPagesData, assetsToLoad } = this.prepareBotPartPages(parts, teamColor, visibleSlots);

    if (assetsToLoad.length > 0) {
      await Assets.load(assetsToLoad);
    }

    const newPagesPromises = newPagesData.map(async (data) => {
      let image: TextureSource | Texture = data.mask && data.swatch
        ? await AssetUtils.loadSwatchShadedBaseTexture(data.alias, data.base, data.mask, data.swatch)
        : Assets.get<Texture>(data.alias).source;

      if (teamColor && data.bakeTeamColor) {
        image = await AssetUtils.tintTeamColor(
          AssetUtils.getTeamColorTintAssetKeyName(data.alias, teamColor.name),
          image,
          teamColor.colour,
          data.mask
        );
      }

      return { [data.key]: { image, originalName: data.originalName } };
    });

    const newPages = Object.assign({}, ...(await Promise.all(newPagesPromises))) as Record<string, PageReplacement>;
    return newPages;
  }

  /**
   * Retrieves the bot part pages based on the provided parts.
   *
   * @param parts - The complete part data set.
   * @returns An object containing the new bot part pages.
   */
  private static getBotPartPages(
    parts: CompletePartDataSet,
    teamColor?: TeamColorAssignment,
    visibleSlots?: BotPartSlot[]
  ): Record<string, PageReplacement> {
    const { newPagesData } = SpineObjectFactory.prepareBotPartPages(parts, teamColor, visibleSlots);

    const newPages = newPagesData.reduce<Record<string, PageReplacement>>((acc, data) => {
      const image = Assets.get<Texture>(data.alias);
      acc[data.key] = { image, originalName: data.originalName };
      return acc;
    }, {});

    return newPages;
  }

  private static getBotAtlasKeyOptions(options?: BotOptions): {
    teamColorName?: string;
    visibleSlots?: BotPartSlot[];
  } {
    return {
      teamColorName: options?.teamColor?.name,
      visibleSlots: options?.visibleSlots
    };
  }

  private static getSourceFromImageResource(resource: IConfigResourceRef): UnresolvedAsset[] {
    let assetData: UnresolvedAsset[];

    switch (resource.type) {
      case EResourceType.image: {
        const src = assetConfigImageLiteral[resource.datasetKey as keyof typeof assetConfigImageLiteral];
        if (!src) {
          throw new Error(`Asset data for ${resource.datasetKey} not found in asset set`);
        }
        assetData = [{
          alias: resource.datasetKey,
          src
        }];
        break;
      }
      default:
        throw new Error(`Resource type ${resource.type} not found in asset set`);
    }

    return assetData;
  }
}
